import CustomModalHeader from 'components/Custom/CustomModalHeader'
import { Modal, ModalBody, Table, Button, FormGroup } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEye,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { useState, useEffect, useCallback } from 'react'
import { SingleDatePicker } from 'react-dates'
import moment from 'moment'
import AxiosInstance from 'utils/AxiosInstance'
import ViewSubmissionModal from './ViewSubmissionModal'
import { useSelector } from 'react-redux'

function ScheduledSubmissionsModal({ showModal, setShowModal, formId }) {
  const [submissions, setSubmissions] = useState([])
  const [selectedDate, setSelectedDate] = useState(moment())
  const [focused, setFocused] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showViewSubmissionModal, setShowViewSubmissionModal] = useState(false)
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [activeTab, setActiveTab] = useState(0)

  const {
    user: { access },
  } = useSelector((store) => store.auth)

  const fetchScheduledSubmissions = useCallback(async () => {
    if (!formId) return

    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (selectedDate) {
        params.append('date', selectedDate.format('YYYY-MM-DD'))
      }

      const response = await AxiosInstance.get(
        `/forms/scheduled-submissions/${formId}?${params.toString()}`
      )

      // Transform the API response to match our component structure
      const transformedSubmissions = response.map((user) => ({
        id: user.user_id,
        firstName: user.first_name,
        lastName: user.last_name,
        status: user.id ? 'submitted' : 'missed', // if form_submission id exists, it's submitted
        submissionId: user.id || null, // form_submission id
        avatar: user.avatar,
      }))

      setSubmissions(transformedSubmissions)
    } catch (error) {
      console.error('Error fetching scheduled submissions:', error)
      setSubmissions([])
    } finally {
      setLoading(false)
    }
  }, [formId, selectedDate])

  useEffect(() => {
    if (showModal && formId) {
      fetchScheduledSubmissions()
    }
  }, [showModal, formId, fetchScheduledSubmissions])

  const handleViewSubmission = (submissionId, userId, userName) => {
    console.log(`Viewing submission ${submissionId} for ${userName}`)
    setSelectedSubmissionId(submissionId)
    setSelectedUserId(userId)
    setShowViewSubmissionModal(true)
    setActiveTab(0)
  }

  const goToPreviousDay = () => {
    const newDate = selectedDate.clone().subtract(1, 'day')
    setSelectedDate(newDate)
  }

  const goToNextDay = () => {
    const newDate = selectedDate.clone().add(1, 'day')
    setSelectedDate(newDate)
  }

  return (
    <Modal
      isOpen={showModal}
      toggle={() => setShowModal(false)}
      size='xl'
      centered
      scrollable
      style={{ minHeight: '80vh' }}
    >
      <CustomModalHeader
        title={'Form Scheduled Submissions'}
        toggle={() => {
          setShowModal(false)
        }}
      />
      <ModalBody
        className='px-4'
        style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '120px' }}
      >
        {/* Date Filter Section */}
        <div
          className='mb-4 p-3'
          style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
          }}
        >
          <div className='d-flex align-items-center justify-content-between'>
            <div className='d-flex align-items-center'>
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className='text-primary mr-2'
              />
              <h6
                className='mb-0'
                style={{ color: '#32325d', fontWeight: '600' }}
              >
                Filter by Date
              </h6>
            </div>
            <div className='d-flex align-items-center'>
              <Button
                color='light'
                size='sm'
                onClick={goToPreviousDay}
                className='mr-2 d-flex align-items-center justify-content-center'
                style={{ width: '32px', height: '32px', padding: '0' }}
              >
                <FontAwesomeIcon icon={faChevronLeft} size='sm' />
              </Button>
              <FormGroup
                className='mb-0 mx-2'
                style={{ position: 'relative', zIndex: 10001 }}
              >
                <SingleDatePicker
                  date={selectedDate}
                  onDateChange={(date) => setSelectedDate(date)}
                  focused={focused}
                  onFocusChange={({ focused }) => setFocused(focused)}
                  id='scheduled-submissions-date-picker'
                  placeholder='Select Date'
                  displayFormat='MM/DD/YYYY'
                  hideKeyboardShortcutsPanel={true}
                  numberOfMonths={1}
                  isOutsideRange={() => false}
                  small
                />
              </FormGroup>
              <Button
                color='light'
                size='sm'
                onClick={goToNextDay}
                className='mr-3 d-flex align-items-center justify-content-center'
                style={{ width: '32px', height: '32px', padding: '0' }}
              >
                <FontAwesomeIcon icon={faChevronRight} size='sm' />
              </Button>
              <div className='ml-3'>
                <small className='text-muted'>
                  Showing submissions for:{' '}
                  <strong>
                    {selectedDate
                      ? selectedDate.format('MM/DD/YYYY')
                      : 'All dates'}
                  </strong>
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className='table-responsive'>
          <Table className='align-items-center table-flush' responsive>
            <thead className='thead-light'>
              <tr>
                <th
                  scope='col'
                  className='px-4 py-3'
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#8898aa',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  User
                </th>
                <th
                  scope='col'
                  className='px-4 py-3'
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#8898aa',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Status
                </th>
                <th
                  scope='col'
                  className='px-4 py-3'
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#8898aa',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan='3' className='text-center py-5'>
                    <div className='d-flex justify-content-center align-items-center'>
                      <div
                        className='spinner-border text-primary mr-3'
                        role='status'
                      >
                        <span className='sr-only'>Loading...</span>
                      </div>
                      <span>Loading submissions...</span>
                    </div>
                  </td>
                </tr>
              ) : submissions.length === 0 ? (
                <tr>
                  <td colSpan='3' className='text-center py-5'>
                    <div className='text-muted'>
                      <p className='mb-0'>
                        No scheduled submissions found for this date.
                      </p>
                      <small>Try selecting a different date.</small>
                    </div>
                  </td>
                </tr>
              ) : (
                submissions.map((submission, index) => (
                  <tr
                    key={submission.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#fff' : '#fafbfc',
                      borderBottom: '1px solid #e9ecef',
                    }}
                  >
                    <td className='px-4 py-3'>
                      <div className='d-flex align-items-center'>
                        <div
                          className='avatar avatar-sm rounded-circle mr-3'
                          style={{
                            backgroundColor: submission.avatar
                              ? 'transparent'
                              : '#5e72e4',
                            minWidth: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {submission.avatar ? (
                            <img
                              src={JSON.parse(submission.avatar).url}
                              alt={`${submission.firstName} ${submission.lastName}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '50%',
                              }}
                              onError={(e) => {
                                // Fallback to initials if image fails to load
                                e.target.style.display = 'none'
                                e.target.nextSibling.style.display = 'flex'
                              }}
                            />
                          ) : null}
                          <span
                            className='text-white font-weight-bold'
                            style={{
                              fontSize: '14px',
                              display: submission.avatar ? 'none' : 'block',
                            }}
                          >
                            {submission.firstName.charAt(0)}
                            {submission.lastName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <span
                            className='font-weight-600'
                            style={{ fontSize: '15px', color: '#32325d' }}
                          >
                            {submission.firstName} {submission.lastName}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className='px-4 py-3'>
                      <span
                        className={`badge badge-pill px-3 py-2 ${
                          submission.status === 'submitted'
                            ? 'text-success'
                            : 'text-danger'
                        }`}
                        style={{
                          backgroundColor:
                            submission.status === 'submitted'
                              ? '#d4edda'
                              : '#f8d7da',
                          border: `1px solid ${
                            submission.status === 'submitted'
                              ? '#c3e6cb'
                              : '#f5c6cb'
                          }`,
                          fontSize: '13px',
                          fontWeight: '600',
                          textTransform: 'capitalize',
                        }}
                      >
                        {submission.status}
                      </span>
                    </td>
                    <td className='px-4 py-3'>
                      {submission.status === 'submitted' &&
                      submission.submissionId ? (
                        <Button
                          color='info'
                          size='sm'
                          className='d-flex align-items-center'
                          onClick={() =>
                            handleViewSubmission(
                              submission.submissionId,
                              submission.id,
                              `${submission.firstName} ${submission.lastName}`
                            )
                          }
                          style={{
                            fontSize: '13px',
                            padding: '6px 12px',
                            fontWeight: '500',
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} className='mr-2' />
                          View Submission
                        </Button>
                      ) : (
                        <span
                          className='text-muted'
                          style={{ fontSize: '13px', fontStyle: 'italic' }}
                        >
                          No submission
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </ModalBody>

      {/* Summary Stats - Fixed Footer */}
      <div
        className='row m-0 p-3'
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: '#fff',
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          zIndex: 1000,
        }}
      >
        <div className='col-md-6'>
          <div className='d-flex align-items-center'>
            <div
              className='icon icon-shape bg-success text-white rounded-circle shadow mr-3'
              style={{ width: '40px', height: '40px' }}
            >
              <span style={{ fontSize: '16px' }}>✓</span>
            </div>
            <div>
              <h5 className='mb-0' style={{ color: '#32325d' }}>
                {submissions.filter((s) => s.status === 'submitted').length}{' '}
                Submitted
              </h5>
              <small className='text-muted'>Users completed the form</small>
            </div>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='d-flex align-items-center'>
            <div
              className='icon icon-shape bg-danger text-white rounded-circle shadow mr-3'
              style={{ width: '40px', height: '40px' }}
            >
              <span style={{ fontSize: '16px' }}>✕</span>
            </div>
            <div>
              <h5 className='mb-0' style={{ color: '#32325d' }}>
                {submissions.filter((s) => s.status === 'missed').length} Missed
              </h5>
              <small className='text-muted'>Users haven't submitted yet</small>
            </div>
          </div>
        </div>
      </div>
      {showViewSubmissionModal && (
        <ViewSubmissionModal
          submissionId={selectedSubmissionId}
          showViewSubmissionModal={showViewSubmissionModal}
          setShowViewSubmissionModal={setShowViewSubmissionModal}
          userId={access === 1 ? selectedUserId : null}
          setActiveTab={setActiveTab}
          activeTab={activeTab}
        />
      )}
    </Modal>
  )
}

export default ScheduledSubmissionsModal
